import { getConfidenceLevel, PROVIDER_DESCRIPTIONS, PROVIDER_NAMES } from "../lib/scoring-utils.ts";
import {
  BUSY_QUEUE_THRESHOLD_MS,
  formatCommunityStage,
  formatPageLabel,
  type ScoringProgress,
} from "../types/scoring.ts";

interface ScoringProgressPanelProps {
  scoringProgressMap: Record<string, ScoringProgress>;
  onDismissTask: (projectId: string) => void;
  founderPrompt: Record<string, { name: string; twitter: string; submitting: boolean }>;
  onFounderPromptChange: (projectId: string, field: "name" | "twitter", value: string) => void;
  onSubmitFounderHint: (projectId: string, handle: string) => void;
  canSubmitFounderHint?: boolean;
}

export function ScoringProgressPanel({
  scoringProgressMap,
  onDismissTask,
  founderPrompt,
  onFounderPromptChange,
  onSubmitFounderHint,
  canSubmitFounderHint = true,
}: ScoringProgressPanelProps) {
  const activeTasks = Object.values(scoringProgressMap).sort((a, b) => b.startedAt - a.startedAt);
  if (activeTasks.length === 0) {
    return null;
  }

  return (
    <div className="scoring-progress-grid">
      {activeTasks.map((task) => {
        const elapsed = Math.floor((Date.now() - task.startedAt) / 1000);
        const completed = task.completedProviders.length;
        const failed = task.failedProviders.length;
        const total = Math.max(task.totalProviders, 1);
        const progress = ((completed + failed) / total) * 100;
        const isBusyQueued =
          task.status === "queued" && (task.waitingMs ?? 0) >= BUSY_QUEUE_THRESHOLD_MS;

        return (
          <div key={task.projectId} className={`scoring-progress ${task.status}`}>
            <div className="scoring-progress-header">
              <div className="scoring-progress-title">
                {(task.status === "queued" || task.status === "processing") && (
                  <div className="spinner-small" />
                )}
                Scoring @{task.handle}
              </div>
              <div className="scoring-progress-meta">
                <span className={`scoring-progress-status ${task.status}`}>{task.status}</span>
                <span className="scoring-progress-time">{elapsed}s</span>
                <button
                  type="button"
                  className="scoring-progress-close"
                  onClick={() => onDismissTask(task.projectId)}
                  aria-label={`Dismiss task for ${task.handle}`}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="scoring-progress-bar">
              <div
                className={`scoring-progress-fill ${task.status === "completed" ? "completed" : task.status === "failed" || task.status === "partial" ? "failed" : ""}`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="scoring-progress-label">
              {completed + failed}/{total} providers
            </div>

            <div className="scoring-progress-hints">
              {task.usingCachedHourlyRun && (
                <span className="progress-hint">Using Cached Hourly Run</span>
              )}
              {task.status === "queued" && task.queuePosition && (
                <span className="progress-hint">Queue Position #{task.queuePosition}</span>
              )}
              {isBusyQueued && (
                <span className="progress-hint busy">
                  Worker Pool Busy - Position #{task.queuePosition ?? "?"}
                </span>
              )}
            </div>

            <div className="scoring-progress-providers">
              {Object.keys(PROVIDER_NAMES).map((slug) => {
                const isCompleted = task.completedProviders.includes(slug);
                const isFailed = task.failedProviders.includes(slug);
                const status = isCompleted ? "success" : isFailed ? "error" : "pending";

                return (
                  <div
                    key={slug}
                    className={`provider-chip ${status}`}
                    title={PROVIDER_DESCRIPTIONS[slug]}
                  >
                    {status === "success" && "✓"}
                    {status === "error" && "✗"}
                    {status === "pending" && "○"}
                    <span>{PROVIDER_NAMES[slug]}</span>
                  </div>
                );
              })}
            </div>

            {task.community && (
              <div className="community-progress-block">
                <div className="community-progress-header">
                  <span>Community Sync</span>
                  <span className="community-progress-stage">
                    {formatCommunityStage(task.community.stage, task.community.status)}
                  </span>
                </div>
                <div className="community-progress-counts">
                  <span>Tweets {task.community.tweetsFetched}</span>
                  <span>Mentions {task.community.mentionsFetched}</span>
                  <span>Replies {task.community.repliesFetched}</span>
                  <span>Quotes {task.community.quotesFetched}</span>
                  <span>Users {task.community.usersEnriched}</span>
                </div>
                <div className="community-progress-queue">
                  {task.community.currentEndpoint
                    ? `${task.community.currentEndpoint} · calls ${task.community.apiCallCount}`
                    : `calls ${task.community.apiCallCount}`}
                  {task.community.currentPage > 0
                    ? ` · page ${formatPageLabel(task.community.currentPage, task.community.currentPageLimit)}`
                    : ""}
                </div>
                {task.community.status === "queued" && task.community.queuePosition && (
                  <div className="community-progress-queue">
                    Queue Position #{task.community.queuePosition}
                  </div>
                )}
              </div>
            )}

            {(task.status === "completed" || task.status === "partial") &&
              task.score !== undefined && (
                <div className="scoring-progress-result">
                  <div className="score-header">
                    <span className="score-result">{task.score.toFixed(1)}</span>
                    <span className="score-label">/ 100</span>
                    {task.status === "partial" && (
                      <span className="score-partial-note">partial</span>
                    )}
                    {task.confidence !== undefined &&
                      (() => {
                        const conf = getConfidenceLevel(task.confidence);
                        return (
                          <span className={`score-confidence ${conf.className}`}>
                            {(task.confidence * 100).toFixed(0)}% confidence
                            <span className="confidence-tier-label">{conf.label}</span>
                          </span>
                        );
                      })()}
                  </div>

                  {task.providerScores &&
                    (() => {
                      const ps = task.providerScores;
                      if (!ps) return null;
                      const clamp = (v: number) => Math.max(0, Math.min(1, v));

                      let socialSum = 0,
                        socialN = 0;
                      const sorsaVal = ps.sorsa ?? ps.tweetscout;
                      if (sorsaVal != null) {
                        socialSum += sorsaVal;
                        socialN++;
                      }
                      if (ps.twitterscore != null) {
                        socialSum += ps.twitterscore;
                        socialN++;
                      }
                      if (ps.moni != null) {
                        socialSum += ps.moni;
                        socialN++;
                      }
                      const baseSocial = socialN > 0 ? socialSum / socialN : 0;
                      const social = clamp(baseSocial) * 20;

                      const backers = clamp(ps.raises ?? 0) * 25;
                      const pedigree = clamp(ps.pedigree ?? 0) * 20;
                      const dev = clamp(ps.github ?? 0) * 5;
                      const momentum = clamp(ps.momentum ?? 0) * 25;
                      const revenue = ps.defillama === 1 ? 5 : 0;

                      const categories = [
                        { label: "Social", pts: social, max: 20 },
                        { label: "Backers", pts: backers, max: 25 },
                        { label: "Pedigree", pts: pedigree, max: 20 },
                        { label: "Momentum", pts: momentum, max: 25 },
                        { label: "Dev", pts: dev, max: 5 },
                        { label: "Revenue", pts: revenue, max: 5 },
                      ].sort((a, b) => b.pts - a.pts);

                      return (
                        <table className="score-table">
                          <thead>
                            <tr>
                              <th>Category</th>
                              <th>Score</th>
                              <th>Max</th>
                            </tr>
                          </thead>
                          <tbody>
                            {categories.map((cat) => (
                              <tr key={cat.label} className="">
                                <td>{cat.label}</td>
                                <td className="mono">{cat.pts.toFixed(1)}</td>
                                <td className="mono dim">/ {cat.max}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}

                  {task.failedProviders.length > 0 && task.failedDetails && (
                    <div className="failed-details-section">
                      <div className="section-label">Failed Providers</div>
                      {task.failedProviders.map((slug) => (
                        <div key={slug} className="failed-detail-row">
                          <span className="failed-name">{PROVIDER_NAMES[slug] ?? slug}</span>
                          <span className="failed-reason">
                            {task.failedDetails?.[slug] ?? "Unknown error"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {task.backers && (
                    <div className="backers-section">
                      <div className="section-label">Backers / Investors</div>
                      <div className="backers-meta">
                        {task.backers.totalFunding != null && (
                          <span className="meta-chip">
                            $
                            {task.backers.totalFunding >= 1_000_000
                              ? `${Math.round(task.backers.totalFunding / 1_000_000)}M`
                              : task.backers.totalFunding >= 1_000
                                ? `${Math.round(task.backers.totalFunding / 1_000)}K`
                                : task.backers.totalFunding}{" "}
                            raised
                          </span>
                        )}
                        {task.backers.latestStage && (
                          <span className="meta-chip">{task.backers.latestStage}</span>
                        )}
                        {task.backers.roundCount != null && task.backers.roundCount > 0 && (
                          <span className="meta-chip">
                            {task.backers.roundCount} round
                            {task.backers.roundCount > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      {task.backers.tierCounts && (
                        <div className="investor-tiers">
                          {task.backers.tierCounts.tier1Names.length > 0 && (
                            <div className="tier-row">
                              <span className="tier-badge tier-1">T1</span>
                              <span>{task.backers.tierCounts.tier1Names.join(", ")}</span>
                            </div>
                          )}
                          {task.backers.tierCounts.tier2Names.length > 0 && (
                            <div className="tier-row">
                              <span className="tier-badge tier-2">T2</span>
                              <span>{task.backers.tierCounts.tier2Names.join(", ")}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {task.backers.allInvestors.length > 0 && (
                        <div className="all-investors">
                          {task.backers.allInvestors.map((name) => (
                            <span key={name} className="investor-chip">
                              {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {task.founder &&
                    (task.founder.founderName || task.founder.founderTwitter) &&
                    (task.founder.confidence == null || task.founder.confidence > 0) && (
                      <div className="founder-section">
                        <div className="section-label">Founder / Pedigree</div>
                        <div className="founder-info">
                          {task.founder.founderName && (
                            <div className="founder-name">
                              {task.founder.founderName}
                              {task.founder.founderTwitter && (
                                <span className="founder-handle">
                                  {" "}
                                  @{task.founder.founderTwitter}
                                </span>
                              )}
                            </div>
                          )}
                          {task.founder.founderTier != null && (
                            <span className={`tier-badge tier-${task.founder.founderTier}`}>
                              Tier {task.founder.founderTier}
                            </span>
                          )}
                        </div>
                        {task.founder.tierReason && (
                          <div className="founder-reason">{task.founder.tierReason}</div>
                        )}
                      </div>
                    )}

                  {canSubmitFounderHint &&
                    (task.status === "completed" || task.status === "partial") &&
                    (!task.founder ||
                      task.founder.confidence === 0 ||
                      task.founder.confidence === null) && (
                      <div className="founder-prompt">
                        <div className="founder-prompt-header">
                          <span className="founder-prompt-icon">?</span>
                          <span className="founder-prompt-title">Founder Not Found</span>
                        </div>
                        <p className="founder-prompt-desc">
                          AI couldn't identify the founder. Provide their info to improve the
                          pedigree score.
                        </p>
                        <div className="founder-prompt-inputs">
                          <input
                            type="text"
                            placeholder="Founder name (e.g., Hayden Adams)"
                            value={founderPrompt[task.projectId]?.name ?? ""}
                            onChange={(e) =>
                              onFounderPromptChange(task.projectId, "name", e.target.value)
                            }
                            disabled={founderPrompt[task.projectId]?.submitting}
                          />
                          <input
                            type="text"
                            placeholder="Twitter handle (e.g., @haydenzadams)"
                            value={founderPrompt[task.projectId]?.twitter ?? ""}
                            onChange={(e) =>
                              onFounderPromptChange(task.projectId, "twitter", e.target.value)
                            }
                            disabled={founderPrompt[task.projectId]?.submitting}
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-small founder-prompt-submit"
                          disabled={
                            founderPrompt[task.projectId]?.submitting ||
                            (!founderPrompt[task.projectId]?.name?.trim() &&
                              !founderPrompt[task.projectId]?.twitter?.trim())
                          }
                          onClick={() => onSubmitFounderHint(task.projectId, task.handle)}
                        >
                          {founderPrompt[task.projectId]?.submitting ? (
                            <>
                              <span className="spinner-small" /> Rescoring...
                            </>
                          ) : (
                            "Rescore Pedigree"
                          )}
                        </button>
                      </div>
                    )}
                </div>
              )}
          </div>
        );
      })}
    </div>
  );
}
